declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

function push(payload: Record<string, unknown>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

export function gtmPushUserId(userId: string) {
  push({ user_id: userId });
}

export function gtmPushSignUp(method: string) {
  push({ event: 'sign_up', method });
}

export function gtmPushReferralSource(source: string, sourceDetail?: string) {
  push({ event: 'referral_source', source, source_detail: sourceDetail ?? null });
}

export function gtmPushFreeSubscription(params: { planId: string; planName: string }) {
  push({ event: 'free_subscription', item_id: params.planId, item_name: params.planName });
}

const REPORTED_PURCHASES_KEY = 'gtm_reported_purchases';
const REPORTED_PURCHASES_CAP = 50;

// localStorage, not sessionStorage: a 3DS popup or redirect can land the success page in a
// different tab, where per-tab storage would read empty and re-push the same purchase.
// Any accessor can throw (Safari private mode) — a storage failure must never block the push.
function reportedIds(): string[] {
  try {
    const raw = localStorage.getItem(REPORTED_PURCHASES_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function hasReported(transactionId: string): boolean {
  return reportedIds().includes(transactionId);
}

// Called only after the push succeeds: marking first would strand the conversion unsent if
// the push threw, with no retry and no error.
function markReported(transactionId: string) {
  try {
    const ids = [...reportedIds(), transactionId].slice(-REPORTED_PURCHASES_CAP);
    localStorage.setItem(REPORTED_PURCHASES_KEY, JSON.stringify(ids));
  } catch {
    // Unwritable storage costs us dedup, not the conversion — nothing to do.
  }
}

export function gtmPushPurchase(params: {
  transactionId: string;
  value: number;
  itemId: string;
  itemName: string;
  currency?: string;
  plan?: string;
  billing?: 'monthly' | 'yearly';
}) {
  // Without a transaction id there is nothing to dedup against, so skip rather than risk a duplicate.
  if (!params.transactionId) return;
  if (hasReported(params.transactionId)) return;

  // GTM merges pushes and only resets on unload, which never happens in an SPA, so clear first or a prior purchase's values bleed into this one.
  push({ ecommerce: null });
  push({
    event: 'purchase',
    ecommerce: {
      transaction_id: params.transactionId,
      value: params.value,
      currency: params.currency ?? 'USD',
      items: [
        {
          item_id: params.itemId,
          item_name: params.itemName,
          price: params.value,
          quantity: 1,
          // item_category separates subscriptions from credit top-ups for conversion filtering.
          item_category: params.plan ? 'subscription' : 'credits',
          ...(params.plan ? { item_variant: params.billing } : {}),
        },
      ],
    },
    ...(params.plan ? { plan: params.plan, billing: params.billing } : {}),
  });

  markReported(params.transactionId);
}
